import { useMemo } from 'react';
import { useActionServed } from '../../engine/meta/useMeta';
import {
  Badge,
  EmptyState,
  ErrorBanner,
  Money,
  Skeleton,
  Stat,
  useApiQuery,
  useInvalidateResource,
  useUpdate,
  type ScreenContext,
  type FieldMeta,
} from '../../engine';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { formatMinutes, sumMinor } from '../../lib/format';
import { field, useNameMap } from './lib';

// Timesheet detail header + entries tab. The generic detail screen renders the lifecycle
// rail (open→submitted→approved→locked) and the submit/approve/lock actions from meta; these
// add the cost/billable summary, the charge-mix bar, and the entry grid that goes read-only
// once the sheet is locked (the DB freezes the entries then).

interface Entry {
  id: string;
  occurred_on: string;
  technician_id: string;
  client_id: string | null;
  minutes: number;
  charge_type: string;
  billable: boolean;
  cost_minor: string;
  billable_amount_minor: string;
  currency: string;
  locked_at: string | null;
}

const CHARGE: { key: string; label: string; color: string }[] = [
  { key: 'tm', label: 'Time & materials', color: 'var(--c-info)' },
  { key: 'prepay', label: 'Prepaid balance', color: 'var(--c-accent)' },
  { key: 'contract_included', label: 'Included in contract', color: 'var(--c-warn)' },
  { key: 'non_billable', label: 'Non-billable', color: 'var(--c-muted)' },
];
const CHARGE_TONE: Record<string, 'info' | 'pos' | 'warn' | 'muted'> = { tm: 'info', prepay: 'pos', contract_included: 'warn', non_billable: 'muted' };

export function useTimesheetEntries(id: string) {
  // A connected system's timesheets (Halo) carry no NovaPSA time entries to list.
  const served = useActionServed('timesheets', ':id/entries');
  return useApiQuery<{ data: Entry[] }>(['timesheet-entries', id], `/v1/timesheets/${id}/entries`, { enabled: Boolean(id) && served });
}

export function TimesheetHeader({ id, row }: ScreenContext) {
  const entriesQ = useTimesheetEntries(id ?? '');
  const rows = useMemo(() => entriesQ.data?.data ?? [], [entriesQ.data]);
  const currency = rows.find((r) => r.currency)?.currency ?? 'USD';
  const inCurrency = rows.filter((r) => (r.currency ?? currency) === currency);
  const mixed = new Set(rows.map((r) => r.currency ?? currency)).size > 1;
  const totalCost = sumMinor(inCurrency, (r) => r.cost_minor);
  const totalBillable = sumMinor(inCurrency, (r) => r.billable_amount_minor);

  const mix = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.charge_type, (m.get(r.charge_type) ?? 0) + r.minutes);
    const total = [...m.values()].reduce((s, n) => s + n, 0);
    return { m, total };
  }, [rows]);

  return (
    <div className="hf-stack">
      <div className="hf-stats" aria-label="Timesheet summary">
        <Stat label="Total cost" value={<Money minor={totalCost} currency={currency} />} sub={mixed ? `${currency} entries only` : undefined} icon="wallet" />
        <Stat label="Total billable" value={<Money minor={totalBillable} currency={currency} />} sub={mixed ? `${currency} entries only` : undefined} icon="receipt" accent />
        <Stat label="Entries" value={<span className="hf-num">{rows.length}</span>} sub={formatMinutes(Number(row?.total_minutes ?? 0))} icon="list" />
      </div>
      {mix.total > 0 && (
        <div className="hf-stack" style={{ gap: 'var(--s-2)' }}>
          <div className="hf-caps">Charge mix</div>
          <div role="img" aria-label="Charge mix" style={{ display: 'flex', height: 8, borderRadius: 'var(--r-full)', overflow: 'hidden', background: 'var(--c-surface-2, var(--c-surface))' }}>
            {CHARGE.filter((c) => (mix.m.get(c.key) ?? 0) > 0).map((c) => (
              <span key={c.key} style={{ width: `${((mix.m.get(c.key) ?? 0) / mix.total) * 100}%`, background: c.color }} />
            ))}
          </div>
          <div className="hf-chiprow">
            {CHARGE.filter((c) => (mix.m.get(c.key) ?? 0) > 0).map((c) => (
              <Badge key={c.key} tone={CHARGE_TONE[c.key]}>{c.label} · {formatMinutes(mix.m.get(c.key))}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const TECH_FIELD = field({ name: 'technician_id', type: 'text' });
const DATE_FIELD = field({ name: 'occurred_on', type: 'date' });
const MIN_FIELD = field({ name: 'minutes', type: 'duration', align: 'right' });
const CHARGE_FIELD = field({ name: 'charge_type', type: 'enum', enum: CHARGE.map((c) => ({ value: c.key, label: c.label, tone: CHARGE_TONE[c.key] })) });
const CLIENT_FIELD: FieldMeta = field({ name: 'client_id', type: 'relation', relation: { resource: 'clients', displayField: 'name' } });

export function TimesheetEntriesTab({ id, row, refresh }: ScreenContext) {
  const entriesQ = useTimesheetEntries(id ?? '');
  const techNames = useNameMap('technicians');
  const clientNames = useNameMap('clients');
  const update = useUpdate('time-entries', { toast: { success: 'Entry updated' } });
  const invalidate = useInvalidateResource();
  const locked = row?.status === 'locked';

  const columns: GridColumn<Entry>[] = [
    { id: 'occurred_on', header: 'Date', field: DATE_FIELD, width: 120, cell: (e) => <span className="hf-num">{e.occurred_on}</span> },
    { id: 'technician_id', header: 'Technician', field: { ...TECH_FIELD, inUpdate: false }, cell: (e) => techNames.get(e.technician_id) ?? e.technician_id.slice(0, 8) },
    { id: 'client_id', header: 'Client', field: CLIENT_FIELD, editable: !locked, cell: (e) => (e.client_id ? (clientNames.get(e.client_id) ?? '—') : <span className="hf-muted">—</span>) },
    { id: 'minutes', header: 'Time', field: { ...MIN_FIELD, inUpdate: false }, align: 'right', cell: (e) => <span className="hf-num">{formatMinutes(e.minutes)}</span> },
    { id: 'charge_type', header: 'Charge', field: { ...CHARGE_FIELD, inUpdate: false } },
    { id: 'cost_minor', header: 'Cost', align: 'right', cell: (e) => <Money minor={e.cost_minor} currency={e.currency} /> },
    { id: 'billable_amount_minor', header: 'Billable', align: 'right', cell: (e) => <Money minor={e.billable_amount_minor} currency={e.currency} /> },
    { id: 'locked', header: '', width: 60, cell: (e) => (e.locked_at || locked ? <Badge tone="pos" dot={false}>locked</Badge> : null) },
  ];

  if (entriesQ.isLoading) return <Skeleton lines={4} />;
  if (entriesQ.error) return <ErrorBanner error={entriesQ.error} />;

  return (
    <DataGrid<Entry>
      aria-label="Time entries"
      columns={columns}
      rows={entriesQ.data?.data ?? []}
      keyOf={(e) => e.id}
      hideToolbar
      empty={<EmptyState title="No time entries" hint={locked ? undefined : 'Log time against this period to see it here.'} icon="timer" />}
      onCellEdit={locked ? undefined : async (entry, f, value) => {
        await update.mutateAsync({ id: entry.id, body: { [f.name]: value } });
        void entriesQ.refetch();
        invalidate('timesheets', id);
        refresh();
      }}
    />
  );
}
